<template>
  <v-card class="route-details-modal">
    <template v-if="isRouteCalculated">
      <template v-if="showTopContent">
        <v-icon class="route-details-close" @click.stop="$emit('close-modal')">
          close
        </v-icon>
        <div v-if="profileStatus === 'agent'" class="client-name">
          client: {{ routeDetails.clientName }}
        </div>
        <div v-if="profileStatus === 'agent'" class="route-name">
          ROUTE:
          <span>{{ routeDetails.name }}</span>
        </div>
        <div :class="['route-state', routeDetails.state]">
          {{ routeDetails.state }}
        </div>
      </template>
      <v-card class="route-details-home" elevation="0" :style="getCardStyle">
        <v-layout v-if="isRouteCalculated" wrap>
          <v-flex
            xs12
            :md6="displayMap"
            :lg7="displayMap"
            :class="{ 'route-homes': showTopContent }"
            class="route-homes-wrapper"
          >
            <div v-if="showTopContent" class="route-info">
              <span>START LOCATION: {{ startLocation }}</span>
              <span>DATE: {{ routeDetails.startTime | dateFormat }}</span>
              <span v-if="isCancelledRoute">N/A</span>
              <span
                v-else
              >START TIME:
                {{
                  routeDetails.calculatedArray[0].arrivalTime | timeFormat
                }}</span>
            </div>
            <div>
              <updated-list-component
                v-if="!isCancelledRoute"
                :class="{ 'route-homes': showTopContent }"
                class="'table-no-border vertical-align-top'"
                :table-class="'no-overflow'"
                :table-headers="routeTableHeaders"
                :table-data="routeTableData"
                @route-details-notification-clicked="
                  updateNotificationState($event)
                "
              />

              <updated-list-component
                v-if="routeUnscheduledTableData.length"
                class="route-homes"
                :class="'table-no-border vertical-align-top'"
                :table-class="'no-overflow'"
                :table-headers="unscheduledTableHeaders"
                :table-data="routeUnscheduledTableData"
              />
            </div>
            <div
              v-if="profileStatus === 'agent' && !hideActions"
              class="route-button-container"
            >
              <v-btn
                v-for="element in generateRouteModalButtons()"
                :key="element.name"
                :outline="element.outline"
                :color="element.color"
                :hidden="
                  isPastRoute(routeDetails) &&
                    (routeDetails.state === 'scheduled' ||
                    routeDetails.state === 'confirmed') &&
                    !element.display
                "
                round
                dark
                class="route-button"
                :class="{
                  'disabled-button':
                    (element.past && isPastRoute(routeDetails)) ||
                    element.reminderSent
                }"
                @click.stop="element.callback"
              >
                {{ element.name }}
              </v-btn>
            </div>
          </v-flex>
          <v-flex xs12 md5 lg4 offset-md1 class="route-map">
            <template v-if="displayMap">
              <google-location-map
                :key="routeDetails._id"
                :route="routeDetails"
                map-height="400px"
              />
              <div
                v-if="displayDirectionBtn && isMobile"
                class="text-xs-center"
              >
                <v-btn
                  class="button-show-direction"
                  :color="colors.hwayzKellyGreen"
                  dark
                  @click.stop="showDirections()"
                >
                  Open Directions
                </v-btn>
              </div>
            </template>
          </v-flex>
        </v-layout>
        <div v-else class="progress-spinner">
          <v-progress-circular indeterminate color="grey darken-1" />
          <div class="small py-3">
            Loading route details...
          </div>
        </div>
      </v-card>
      <v-dialog
        v-model="confirmationModal.status"
        :width="alert.width"
        :max-width="alert.maxWidth"
      >
        <confirmation-modal
          :modal-options="confirmationModal"
          @on-negative-clicked="closeDeleteConfirmation"
          @on-positive-clicked="cancelRoute"
        />
      </v-dialog>
    </template>
    <div v-else class="progress-spinner">
      <v-progress-circular indeterminate color="grey darken-1" />
      <div class="small py-3">
        Loading route details...
      </div>
    </div>
  </v-card>
</template>

<script>
import { last } from 'lodash'
import moment from 'moment'
import { mapState } from 'vuex'
import UpdatedListComponent from '../uiUtils/UpdatedListComponent.vue'
import GoogleLocationMap from '../googlemap/GoogleLocationMap.vue'
import ConfirmationModal from '../modals/ConfirmationModal.vue'
import routeDataContent from '../mixins/routeDataContent'
import { sendRouteSchedule } from '../../services/messagesService'
import {
  displayRouteDirections,
  isActiveRouteStop,
  getStartLocationAddressText,
  isSendReminderActive,
  isConfirmedOrScheduledRoute,
  isCancelledRoute
} from '../../utils/routeUtils'
import { isEmpty } from '../../utils/generalUtils'
import colors from '../../scss/_colors.scss'
import { propertyFullAddress } from '../../utils/propertyUtils'
import { routeDirectionCalculateCheck } from '../../utils/planRouteUtils'
import {
  requestRouteShowing,
  updateSavedRoutesOnPendingRequest,
  sendScheduleRequest,
  createCalendarTimeFrame,
  cancelDeleteRoute
} from '../../services/routeService'

export default {
  components: { UpdatedListComponent, GoogleLocationMap, ConfirmationModal },

  filters: {
    dateFormat: value => {
      return moment(value).format('dddd MM-DD-YY')
    },
    timeFormat: value => {
      return value ? moment(value).format('h:mm A') : 'N/A'
    }
  },

  mixins: [routeDataContent],

  props: {
    routeDetails: {
      type: Object,
      default: () => {}
    },
    hideActions: {
      type: Boolean,
      default: false
    },
    showTopContent: {
      type: Boolean,
      default: true
    }
  },

  data() {
    return {
      colors,
      routeTableHeaders: [
        { text: 'Home Address', value: 'address', sortable: false },
        {
          text: 'Time',
          value: 'time',
          sortable: false,
          width: '20%',
          align: 'center'
        },
        {
          text: 'Status',
          value: 'status',
          align: 'center',
          width: '20%',
          sortable: false
        }
      ],
      unscheduledTableHeaders: [
        { text: 'Unscheduled', value: 'address', sortable: false },
        {
          text: 'Status',
          value: 'status',
          align: 'center',
          width: '40%',
          sortable: false
        }
      ],
      confirmationModal: {
        status: false,
        message: null,
        positive: null,
        negative: null
      }
    }
  },

  computed: {
    ...mapState('style', ['modal', 'alert']),
    ...mapState('common', ['profileStatus', 'isMobile']),

    displayMap() {
      return (
        this.isRouteCalculated && !isEmpty(this.routeDetails.calculatedArray)
      )
    },

    isCancelledRoute() {
      return isCancelledRoute(this.routeDetails)
    },

    startLocation() {
      if (this.isRouteCalculated) {
        return this.routeDetails.calculatedArray &&
          this.routeDetails.calculatedArray.length > 0
          ? getStartLocationAddressText(
            this.routeDetails.calculatedArray[0].Address
          )
          : 'N/A'
      }
      return 'Loading...'
    },

    isRouteCalculated() {
      const value =
        !isEmpty(this.routeDetails) &&
        !isEmpty(this.routeDetails.fullCalculatedArray)
      return value
    },

    getCardStyle() {
      if (this.isMobile) {
        return {
          'min-height': '200px',
          margin: '2rem 3rem'
        }
      } else if (this.showTopContent) {
        return {
          'min-height': '500px',
          margin: '2rem 3rem',
          padding: '0 4rem'
        }
      } else {
        return {
          'min-height': '200px'
        }
      }
    },

    displayDirectionBtn() {
      if (isCancelledRoute(this.routeDetails)) return false
      return this.displayMap && this.routeDetails.calculatedArray.length > 1
    },

    routeTableData() {
      if (
        this.isRouteCalculated &&
        !isEmpty(this.routeDetails.calculatedArray)
      ) {
        const tableData = [...this.routeDetails.calculatedArray]

        tableData.forEach(item => {
          const history = last(item.history)
          if (history && history.message) {
            item.agent_message = history.message
          }
        })
        return this.generateTableItems(tableData)
      }
      return []
    },

    routeUnscheduledTableData() {
      if (this.isRouteCalculated) {
        const tableData = [...this.routeDetails.fullCalculatedArray].filter(
          stop => !isActiveRouteStop(stop)
        )
        tableData.forEach(item => {
          const history = last(item.history)
          if (history && history.message) {
            item.agent_message = history.message
          }
        })
        return this.generateTableItems(tableData, true)
      }
      return []
    }
  },

  watch: {
    routeDetails: {
      handler: async function(newValue) {
        if (!isEmpty(newValue)) {
          await this.initRouteDataDisplay(newValue)
        }
      }
    }
  },

  async mounted() {
    if (!isEmpty(this.routeDetails)) {
      await this.initRouteDataDisplay(this.routeDetails)
    }
  },

  methods: {
    async initRouteDataDisplay(routeData) {
      const updatedRoute = routeDirectionCalculateCheck(routeData)
        ? await this.calculateRouteHomeMarkerArray(routeData)
        : { ...routeData }
      this.generateTableItems(
        updatedRoute.calculatedArray ? updatedRoute.calculatedArray : []
      )
    },

    showDirections() {
      displayRouteDirections(this.routeDetails.calculatedArray)
    },

    generateTableItems(fromArray, isUnscheduled = false) {
      if (isEmpty(this.routeDetails.fullCalculatedArray)) return []
      let displayArray = [...fromArray]

      if (!isEmpty(displayArray)) {
        let response = []
        let alphabet = 'A'
        let counter = 0
        displayArray.map((item, index) => {
          if (item) {
            let obj = {}
            let adjustedLetter = alphabet
            if (item.arrivalTime) {
              adjustedLetter = String.fromCharCode(
                alphabet.charCodeAt(0) + counter
              )
              counter++
            }
            const houseName = this.routeDetails.fullCalculatedArray.find(
              house => house._id === item.propertyId
            )
            obj[0] = {
              type: 'route-address',
              class: 'textLeft',
              text:
                item.Address ||
                (houseName ? propertyFullAddress(houseName) : ''),
              preText:
                item.arrivalTime && fromArray.length > 1 ? adjustedLetter : '',
              preTextClass: 'hwayzBlue font-weight-medium title',
              data: item
            }

            if (isUnscheduled) {
              obj[1] = {
                text: this.getStopState(item.state),
                class: item.state
              }
            } else {
              obj[1] = {
                text:
                  item.arrivalTime !== 'Invalid date'
                    ? moment(item.arrivalTime).format('hh:mm A')
                    : 'Loading...'
              }
              obj[2] = {
                text: this.getStopState(item.state),
                class: item.state
              }
            }
            if (
              item.arrivalTime ||
              this.profileStatus === 'agent' ||
              (this.profileStatus === 'client' &&
                isCancelledRoute(this.routeDetails))
            ) {
              if (item.agent_message) {
                Object.entries(obj).forEach(([key, value]) => {
                  obj[key].cellClass = 'agent-message'
                })
              }
              response.push(obj)
            }
          }
        })
        return response
      }
      return []
    },

    generateRouteModalButtons() {
      if (this.routeDetails) {
        const { state } = this.routeDetails
        switch (state) {
          case 'saved':
            return [
              {
                name: 'cancel route',
                outline: true,
                color: colors.secondary,
                callback: this.openDeleteConfirmation
              },
              {
                name: 'revise route',
                outline: true,
                color: colors.primary,
                past: true,
                callback: () => this.$emit('revise-route', this.routeDetails)
              },
              {
                name: 'request showing',
                color: colors.pending,
                past: true,
                callback: this.requestShowing
              }
            ]
          case 'scheduled':
            let returnValue = [
              {
                name: 'cancel route',
                outline: true,
                color: colors.secondary,
                display: true,
                callback: this.openDeleteConfirmation
              }
            ]
            isSendReminderActive(this.routeDetails)
              ? returnValue.push({
                name: 'reminder sent',
                color: 'green',
                past: true,
                outline: true,
                reminderSent: true,
                callback: () => this.sendSchedule(true)
              })
              : returnValue.push({
                name: 'send reminder',
                color: 'green',
                past: true,
                callback: () => this.sendSchedule(true)
              })
            return returnValue
          case 'confirmed':
            return [
              {
                name: 'cancel route',
                outline: true,
                color: colors.secondary,
                display: true,
                callback: this.openDeleteConfirmation
              },
              {
                name: 'send schedule',
                color: 'purple',
                past: true,
                callback: () => this.sendSchedule()
              }
            ]
          default:
            return []
        }
      }
      return []
    },

    getStopState(defaultState) {
      switch (defaultState) {
        case 'decline':
          return 'declined'
        case 'accept':
          return 'accepted'
        case 'notscheduled':
          return 'unanswered/unscheduled'
        default:
          return defaultState
      }
    },

    // Route details modal button functionalities
    openDeleteConfirmation() {
      Object.assign(this.confirmationModal, {
        status: true,
        message: isConfirmedOrScheduledRoute(this.routeDetails)
          ? `Are you sure you want to cancel this route?`
          : `All data associated with this route will be lost. Are you sure you want to delete this route? `,
        positive: 'Yes, cancel route',
        negative: 'No, not yet'
      })
    },

    // Functionality to delete the route from route modal button
    async cancelRoute() {
      this.closeDeleteConfirmation()
      const responseObject = await cancelDeleteRoute(this.routeDetails, this.clientData)
      if (!isEmpty(responseObject)) {
        const { isSaved, data } = responseObject
        if (!isSaved) {
          this.updateRhtAfterCancelRoute(data)
          this.updateAgentRoutesOnCancel(data)
        } else {
          this.setAgentRouteData(
            this.agentRouteData.filter(route => route._id !== data._id)
          )
        }
        this.updateClientDataOnCancel(this.clientData, data, isSaved)
        this.$emit('refresh-data', {
          type: 'success',
          value: true,
          message: `Successfully cancelled the route ${data.name}`
        })
      }
    },

    closeDeleteConfirmation() {
      Object.assign(this.confirmationModal, {
        status: false,
        message: null
      })
    },

    async requestShowing() {
      const response = await requestRouteShowing(this.routeDetails)

      if (response.status === 200) {
        const clientData = this.agentData.clients.find(
          client => client._id === this.routeDetails.clientId
        )

        const message = updateSavedRoutesOnPendingRequest(
          clientData,
          response.data
        )

        const updatedRoute = {
          ...response.data,
          ...createCalendarTimeFrame(response.data)
        }
        this.routesUpdateAfterActions(
          updatedRoute,
          clientData,
          this.agentRouteData
        )
        this.updateRhtAfterRequestShowing(response.data)

        this.$emit('refresh-data', {
          type: 'success',
          value: true,
          message
        })
      }
    },

    async sendSchedule(isReminder = false) {
      const clientData = this.agentData.clients.find(
        client => client._id === this.routeDetails.clientId
      )
      const response = await sendScheduleRequest(this.routeDetails)
      if (response.status === 200) {
        let updatedRoute = {
          ...response.data,
          ...createCalendarTimeFrame(response.data)
        }

        this.routesUpdateAfterActions(
          updatedRoute,
          clientData,
          this.agentRouteData
        )

        const subject = isReminder ? 'Schedule Reminder' : `Scheduled Route`
        await sendRouteSchedule({
          to: [this.routeDetails.clientId],
          message: 'Route Details',
          date: moment(this.routeDetails.startTime).format('LLLL'),
          subject,
          routeId: this.routeDetails._id
        })
        this.$emit('refresh-data', {
          type: 'success',
          value: true,
          message: `Successfully sent route schedule to client ${this.routeDetails.clientName}`,
          modalState: true
        })
      }
    }
  }
}
</script>

<style lang="sass">
.button-show-direction
  font-size: 1.4rem
  padding-left: 1rem
  padding-right: 1rem
</style>

<style lang="sass" scoped>
.route-details-modal
  padding: 4rem 3rem
  min-height: 30rem

.route-details-close
  @extend %modal__button-close, %disable-selection

.client-name
  text-transform: uppercase
  font-size: $hwayzFont1rem6
  font-weight: 600
  padding-bottom: 2rem

.route-name
  font-weight: 600
  span
    text-transform: capitalize
    font-size: 1.6rem

.route-state
  text-transform: uppercase
  font-weight: 600
  padding-top: 2rem
  font-size: $hwayzFont1rem4

.route-homes
  padding: 2rem 0

.route-info
  text-align: left
  display: flex
  flex-flow: column
  span
    margin-bottom: 1rem

.route-button-container
  margin: 2rem 0

.route-button
  height: 2.7rem
  font-size: 1rem
  margin-right: 2rem

.progress-spinner
  padding-top: 8rem
  text-align: center

.disabled-button
  @extend %disabled-state

.saved
  color: $saved
.pending
  color: $hwayzYellow
.scheduled
  color: $hwayzGreen
.decline
  color: $decline
.confirmed
  color: $hwayzBlue
.completed
  color: $completed
.cancelled
  color: $hwayzDarkGray
</style>
